import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { queryAccounts } from "../Api/accountQueries";
import type { AccountPage, AccountQuery } from "../Types/accountQuery";

const TTL = 60_000;
const pages = new Map<
  string,
  { time: number; scope: string; value: AccountPage }
>();
const revisions = new Map<string, number>();
const firstPageRevisions = new Map<string, number>();
const listeners = new Set<() => void>();
const requests = new Map<string, { controller: AbortController; revision: number; users: number; promise: Promise<AccountPage> }>();
export function clearAccountQueryCache() {
  for (const request of requests.values()) request.controller.abort();
  requests.clear();
  pages.clear();
  revisions.clear();
  firstPageRevisions.clear();
  for (const notify of listeners) notify();
}
function acquirePage(query: AccountQuery, key: string, revision: number) {
  let request = requests.get(key);
  if (request && request.revision !== revision) {
    request.controller.abort();
    requests.delete(key);
    request = undefined;
  }
  if (!request) {
    const controller = new AbortController();
    request = { controller, revision, users: 0, promise: queryAccounts(query, controller.signal) };
    requests.set(key, request);
  }
  const shared = request;
  shared.users += 1;
  return {
    ...shared,
    release: () => {
      shared.users -= 1;
      if (!shared.users) {
        shared.controller.abort();
        if (requests.get(key) === shared) requests.delete(key);
      }
    },
  };
}
export const queryScopeKey = (q: Pick<AccountQuery, "user_id" | "client_id">) =>
  `${q.user_id}:${q.client_id}`;
export const accountQueryKey = (q: AccountQuery) =>
  JSON.stringify(["account-query-v1", q]);
export function invalidateAccountQueries(client: string) {
  for (const [key, page] of pages)
    if (page.scope.endsWith(`:${client}`)) pages.delete(key);
  revisions.set(client, (revisions.get(client) ?? 0) + 1);
  for (const notify of listeners) notify();
}
/** Background delivery never replaces a later-page snapshot. */
export function invalidateAccountFirstPages(client: string) {
  for (const [key, page] of pages) {
    const query = JSON.parse(key)[1] as AccountQuery;
    if (page.scope.endsWith(`:${client}`) && query.offset === 0) pages.delete(key);
  }
  firstPageRevisions.set(client, (firstPageRevisions.get(client) ?? 0) + 1);
  for (const notify of listeners) notify();
}
const pageRevision = (query: AccountQuery) =>
  (revisions.get(query.client_id) ?? 0) +
  (query.offset === 0 ? firstPageRevisions.get(query.client_id) ?? 0 : 0);
const subscribe = (notify: () => void) => {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
};
export function useAccountQueryRevision(client: string) {
  return useSyncExternalStore(subscribe, () => revisions.get(client) ?? 0);
}
export function rememberAccountPage(
  q: AccountQuery,
  value: AccountPage,
  time = Date.now(),
) {
  const key = accountQueryKey(q),
    scope = queryScopeKey(q);
  pages.delete(key);
  pages.set(key, { time, scope, value });
  const keys = [...pages]
    .filter(([, page]) => page.scope === scope)
    .map(([key]) => key);
  while (keys.length > 20) pages.delete(keys.shift()!);
  // Drop data from other signed-in users instead of retaining their cached rows.
  for (const [key, page] of pages)
    if (!page.scope.startsWith(`${q.user_id}:`) || time - page.time > TTL)
      pages.delete(key);
}
export function cachedAccountPage(q: AccountQuery, time = Date.now()) {
  const page = pages.get(accountQueryKey(q));
  return page && time - page.time < TTL ? page.value : undefined;
}
export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}
export function useAccountQuery(query: AccountQuery, enabled = true) {
  const key = accountQueryKey(query),
    revision = useSyncExternalStore(subscribe, () => pageRevision(query));
  const [visible, setVisible] = useState(!document.hidden);
  useEffect(() => {
    const focus = () => {
      if (!document.hidden && enabled && query.offset === 0)
        invalidateAccountFirstPages(query.client_id);
    };
    const visibility = () => {
      setVisible(!document.hidden);
      if (!document.hidden) focus();
    };
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [enabled, query.client_id, query.offset]);
  const current = useRef({ key, revision, enabled });
  useLayoutEffect(() => {
    current.current = { key, revision, enabled };
  }, [key, revision, enabled]);
  const [state, setState] = useState<{
    key: string;
    revision: number;
    data?: AccountPage;
    loading: boolean;
    error: string;
  }>({ key: "", revision: -1, loading: enabled, error: "" });
  const cached = enabled ? cachedAccountPage(query) : undefined;
  // Retain a cache hit as this hook's displayed page before it can be invalidated.
  if (cached && (state.key !== key || state.data !== cached)) {
    setState({ key, revision, data: cached, loading: false, error: "" });
  }
  useEffect(() => {
    if (!enabled || !visible) return;
    const q: AccountQuery = JSON.parse(key)[1];
    const cached = cachedAccountPage(q);
    if (cached && q.offset !== 0) return;
    const request = acquirePage(q, key, revision);
    const controller = request.controller;
    let active = true;
    request.promise
      .then((data) => {
        if (
          !active || controller.signal.aborted ||
          current.current.key !== key ||
          current.current.revision !== revision ||
          pageRevision(q) !== revision ||
          !current.current.enabled
        )
          return;
        rememberAccountPage(q, data);
        setState({ key, revision, data, loading: false, error: "" });
      })
      .catch((error: Error) => {
        if (
          active && !controller.signal.aborted &&
          current.current.key === key &&
          current.current.revision === revision
        )
          setState(previous => ({ key, revision,
            data: previous.key === key && ![401, 403].includes((error as Error & { status?: number }).status ?? 0) ? previous.data : undefined,
            loading: false, error: error.message }));
      });
    return () => { active = false; request.release(); };
  }, [key, revision, enabled, visible]);
  const valid = state.key === key && state.revision === revision;
  const data = enabled
    ? (cached ?? (state.key === key ? state.data : undefined))
    : undefined;
  const fetching = enabled && !cached && (!valid || state.loading);
  return {
    data,
    loading: fetching && !data,
    refreshing: fetching && !!data,
    error: valid ? state.error : "",
    refetch: () => invalidateAccountQueries(query.client_id),
  };
}
